<template>
  <div ref="rootEl" class="p-textarea-mention relative">
    <UPopover
      :open="open && isEditable"
      :modal="false"
      :content="{
        side: 'bottom',
        align: 'start',
        sideOffset: 4,
        collisionPadding: 8,
        onOpenAutoFocus: (event) => event.preventDefault(),
        onCloseAutoFocus: (event) => event.preventDefault(),
        onInteractOutside
      }"
      :ui="{
        content:
          'z-50 w-(--reka-popover-trigger-width) max-w-[calc(100vw-1rem)] bg-transparent shadow-none ring-0'
      }"
      @update:open="onPopoverOpenChange"
    >
      <template #anchor>
        <div>
          <PTextarea
            ref="textarea"
            v-bind="mergeProps($attrs, textareaProps)"
            :model-value="displayText"
            :placeholder="placeholderWithHint"
            :aria-label="$attrs['aria-label'] ?? placeholder"
            :aria-expanded="open && isEditable"
            :aria-controls="open && isEditable ? suggestionsId : undefined"
            :aria-activedescendant="activeSuggestionId"
            :title="hoveredMentionTitle ?? $attrs.title"
            :aria-description="
              $attrs['aria-description'] ??
              (hasMentions
                ? 'Klik op een mention om te openen, of gebruik Ctrl+Enter of Command+Enter bij de cursor.'
                : undefined)
            "
            :ui="{
              ...props.ui,
              base: normalizeClass([
                props.ui?.base,
                props.class,
                $attrs.class,
                hasMentions && [
                  'caret-on-surface dark:caret-on-surface-dark text-transparent!',
                  hoveredMentionTitle && !hoveredMentionIsSaving && !disabled
                    ? 'cursor-pointer'
                    : undefined
                ]
              ])
            }"
            aria-autocomplete="list"
            inputmode="email"
            autocomplete="off"
            autocapitalize="sentences"
            data-1p-ignore
            data-lpignore="true"
            data-bwignore
            @beforeinput="handleBeforeInput"
            @input="onInput"
            @keydown="onKeydown"
            @click="!disabled && handleMentionClick($event)"
            @dblclick="!disabled && handleMentionClick($event, true)"
            @mousemove="onMentionHover"
            @mouseleave="hoveredMentionStart = null"
            @scroll="onEditorScroll"
            @paste="onPaste"
            @focusin="isFocused = true"
            @focusout="handleFocusout"
            @blur="emit('blur', $event)"
            @change="emit('change', $event)"
          >
            <template v-if="$slots.leading" #leading="slotProps">
              <slot name="leading" v-bind="slotProps" />
            </template>
            <template #default="slotProps">
              <span
                v-if="hasMentions"
                aria-hidden="true"
                class="pointer-events-none absolute overflow-hidden rounded-md"
                :style="mentionFrameStyle"
                data-slot="mention-display"
              >
                <span
                  class="text-on-surface dark:text-on-surface-dark block"
                  :style="mentionTextStyle"
                  ><span
                    v-for="(segment, index) in displaySegments"
                    :key="index"
                    :data-mention-start="segment.type === 'mention' ? segment.start : undefined"
                    :class="
                      segment.type === 'mention'
                        ? [
                            mentionLinkClass(segment.kind, !isSavingLocation(segment)),
                            '[font-weight:inherit]! [-webkit-text-stroke:0.35px]'
                          ]
                        : undefined
                    "
                    >{{ segment.type === "mention" ? segment.label : segment.value }}</span
                  ></span
                >
              </span>
              <slot v-bind="slotProps" />
            </template>
            <template v-if="$slots.trailing" #trailing="slotProps">
              <slot name="trailing" v-bind="slotProps" />
            </template>
          </PTextarea>
        </div>
      </template>
      <template #content>
        <ul
          ref="dropdownEl"
          :id="suggestionsId"
          role="listbox"
          aria-label="Mention suggesties"
          data-testid="location-dropdown"
          class="surface ring-gray-lighten-2 dark:ring-gray-lighten-2-dark max-h-[min(32rem,var(--reka-popover-content-available-height))] overflow-auto rounded-md py-1 text-sm shadow-lg ring-1 focus:outline-none"
          @mousedown.prevent
          @scroll="onDropdownScroll"
        >
          <template v-if="selectorMode === 'at'">
            <li
              v-if="loading && atItems.length === 0"
              class="text-gray-lighten-1 dark:text-gray-lighten-1-dark px-2 py-1"
            >
              Zoeken…
            </li>
            <li
              v-else-if="atItems.length === 0"
              class="text-gray-lighten-1 dark:text-gray-lighten-1-dark px-2 py-1"
            >
              {{ atEmptyText }}
            </li>
            <template v-for="(item, index) in atItems" :key="item.key">
              <!-- Rental returns look and highlight like in "Product toevoegen" of the instruction processing -->
              <li
                v-if="item.rentalIntake"
                data-testid="at-mention-suggestion"
                :id="`${suggestionsId}-${index}`"
                role="option"
                :aria-selected="index === highlightedIndex"
                :data-mention-mode="item.mode"
                :data-highlighted="index === highlightedIndex ? 'true' : 'false'"
                class="cursor-pointer select-none"
                :class="index === highlightedIndex ? 'primary' : ''"
                @mouseenter="highlightedIndex = index"
                @mousedown.prevent="selectAtSuggestion(item)"
              >
                <MentionRentalReturnSuggestion
                  class="block py-2 pl-2"
                  :rental-intake="item.rentalIntake"
                  :tagged-project="rentalProject"
                />
              </li>
              <!-- Same look as "+ Nieuw persoon" in the dienstverlener combobox -->
              <li
                v-else-if="item.isCreateOption"
                data-testid="at-mention-suggestion"
                :id="`${suggestionsId}-${index}`"
                role="option"
                :aria-selected="index === highlightedIndex"
                :data-mention-mode="item.mode"
                :data-highlighted="index === highlightedIndex ? 'true' : 'false'"
                class="flex cursor-pointer items-center px-1 py-2 select-none"
                :class="index === highlightedIndex ? 'primary' : ''"
                @mouseenter="highlightedIndex = index"
                @mousedown.prevent="selectAtSuggestion(item)"
              >
                <FontAwesomeIcon :icon="['fas', 'plus']" size="xs" class="mr-1" />
                <span class="truncate">{{ item.label }}</span>
              </li>
              <li
                v-else
                data-testid="at-mention-suggestion"
                :id="`${suggestionsId}-${index}`"
                role="option"
                :aria-selected="index === highlightedIndex"
                :data-mention-mode="item.mode"
                :data-highlighted="index === highlightedIndex ? 'true' : 'false'"
                class="hover:bg-primary-lighten-1/50 hover:text-on-primary-lighten-1 dark:hover:bg-primary-lighten-1-dark/50 dark:hover:text-on-primary-lighten-1-dark flex cursor-pointer items-center gap-x-2 px-2 py-1 select-none"
                :class="
                  index === highlightedIndex
                    ? 'bg-primary-lighten-1/50 text-on-primary-lighten-1 dark:bg-primary-lighten-1-dark/50 dark:text-on-primary-lighten-1-dark'
                    : ''
                "
                @mouseenter="highlightedIndex = index"
                @mousedown.prevent="selectAtSuggestion(item)"
              >
                <span
                  v-if="!item.command"
                  class="text-primary dark:text-primary-dark text-2xs inline-block w-12 shrink-0 tracking-wide uppercase"
                >
                  {{ AT_MENTION_MODE_LABELS[item.mode] }}
                </span>
                <span class="truncate" :class="item.command ? 'font-semibold' : ''">
                  {{ item.label }}
                </span>
                <span
                  v-if="item.description"
                  class="text-gray-lighten-1 dark:text-gray-lighten-1-dark truncate text-xs"
                >
                  {{ item.description }}
                </span>
                <!-- The command's create variant (e.g. @project+) on the same row -->
                <PButton
                  v-if="item.createCommand"
                  variant="ghost"
                  size="sm"
                  tabindex="-1"
                  data-testid="at-mention-create-command"
                  class="hover:text-primary hover:dark:text-primary-dark text-2xs text-gray-lighten-1 dark:text-gray-lighten-1-dark ml-auto shrink-0 hover:underline"
                  :title="item.createDescription"
                  @mousedown.prevent.stop="selectAtSuggestion(item, item.createCommand)"
                >
                  @{{ item.createCommand }} voor nieuw
                </PButton>
              </li>
            </template>
            <li
              v-if="loading && atItems.length > 0"
              class="text-gray-lighten-1 dark:text-gray-lighten-1-dark px-2 py-1"
            >
              Laden…
            </li>
          </template>

          <template v-else>
            <!-- `@locatie` lists saved locations right away instead -->
            <li
              v-if="trimmedQuery.length < MIN_CHARS && locationTrigger !== 'command'"
              class="text-gray-lighten-1 dark:text-gray-lighten-1-dark px-2 py-1"
            >
              Typ <UKbd value="/" size="sm" /> of druk twee keer op <UKbd size="sm">spatie</UKbd> om
              een locatie te zoeken. Gebruik minimaal {{ MIN_CHARS }} tekens.
            </li>
            <li
              v-else-if="loading && items.length === 0"
              data-testid="location-search-loading"
              class="text-gray-lighten-1 dark:text-gray-lighten-1-dark px-2 py-1"
            >
              <span class="flex items-center gap-x-2">
                <PProgress
                  as="span"
                  size="sm"
                  aria-label="Locaties worden gezocht"
                  class="w-12! shrink-0"
                />
                Zoeken…
              </span>
            </li>
            <li
              v-else-if="items.length === 0"
              class="text-gray-lighten-1 dark:text-gray-lighten-1-dark px-2 py-1"
            >
              Geen locaties gevonden
            </li>
            <li
              v-for="(item, index) in items"
              :key="item.key"
              :id="`${suggestionsId}-${index}`"
              role="option"
              :aria-selected="index === highlightedIndex"
              data-testid="location-suggestion"
              :data-source="item.source"
              :data-highlighted="index === highlightedIndex ? 'true' : 'false'"
              class="hover:bg-primary-lighten-1/50 hover:text-on-primary-lighten-1 dark:hover:bg-primary-lighten-1-dark/50 dark:hover:text-on-primary-lighten-1-dark flex cursor-pointer items-center gap-x-2 px-2 py-1 select-none"
              :class="
                index === highlightedIndex
                  ? 'bg-primary-lighten-1/50 text-on-primary-lighten-1 dark:bg-primary-lighten-1-dark/50 dark:text-on-primary-lighten-1-dark'
                  : ''
              "
              @mouseenter="highlightedIndex = index"
              @mousedown.prevent="selectSuggestion(item)"
            >
              <span
                class="text-2xs inline-block w-12 shrink-0 tracking-wide uppercase"
                :class="
                  item.source === 'saved'
                    ? 'text-secondary dark:text-secondary-dark'
                    : 'text-gray-lighten-1 dark:text-gray-lighten-1-dark'
                "
              >
                {{ item.source === "saved" ? "Opgesl." : "Maps" }}
              </span>
              <span class="truncate">{{ item.label }}</span>
            </li>
            <!-- Saved locations are shown while the Maps suggestions are still coming -->
            <li
              v-if="loading && items.length > 0"
              data-testid="location-search-loading"
              class="text-gray-lighten-1 dark:text-gray-lighten-1-dark flex items-center gap-x-2 px-2 py-1"
            >
              <PProgress
                as="span"
                size="sm"
                aria-label="Locaties worden gezocht"
                class="w-12! shrink-0"
              />
              Zoeken…
            </li>
          </template>
        </ul>
      </template>
    </UPopover>

    <LocationMapModal
      v-if="mapLocation"
      :inline="usesInlineLocationMap()"
      :location="mapLocation"
      @saved="onMapSaved"
      @close="closeMap"
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  normalizeClass,
  mergeProps,
  nextTick,
  ref,
  useId,
  useTemplateRef,
  watch,
  watchEffect,
  type CSSProperties
} from "vue"
import { useResizeObserver } from "@vueuse/core"
import UPopover from "@nuxt/ui/components/Popover.vue"
import UKbd from "@nuxt/ui/components/Kbd.vue"
import PButton from "@/components/elements/PButton.vue"
import PTextarea from "./PTextarea.vue"
import type { PTextareaProps, PTextareaSlots } from "./textarea"
import LocationMapModal from "@/components/mention/LocationMapModal.vue"
import MentionRentalReturnSuggestion from "@/components/mention/MentionRentalReturnSuggestion.vue"
import PProgress from "@/components/elements/PProgress.vue"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import {
  AT_MENTION_PLACEHOLDER_HINT,
  isPendingLocationId,
  useMentionEditor,
  type AtMentionMode
} from "@/composables/mention/use-mention"

defineOptions({ inheritAttrs: false })
defineSlots<PTextareaSlots>()

const AT_MENTION_MODE_LABELS: Record<AtMentionMode, string> = {
  contact: "Persoon",
  project: "Project",
  business: "Bedrijf",
  rental: "Retour",
  location: "Locatie"
}

const props = withDefaults(
  defineProps<
    PTextareaProps & { transformClipboard?: (value: string) => string; apiKey?: string }
  >(),
  { rows: 1, autoresize: true, autofocus: false, disabled: false, readonly: false }
)

const emit = defineEmits<{
  input: [event: Event]
  blur: [event: FocusEvent]
  change: [event: Event]
  "file-trigger": []
}>()
const text = defineModel<string | null | undefined>({ default: "" })
const textarea = useTemplateRef<InstanceType<typeof PTextarea>>("textarea")
const textareaProps = computed(() => {
  const forwarded = { ...props }
  delete forwarded.transformClipboard
  delete forwarded.apiKey
  return forwarded
})
const isEditable = computed(() => !props.disabled && !props.readonly)
/** Keep the placeholder compact outside the field; show mention commands only while editing. */
const placeholderWithHint = computed(() =>
  isEditable.value && isFocused.value
    ? [props.placeholder, AT_MENTION_PLACEHOLDER_HINT].filter(Boolean).join("\n")
    : (props.placeholder ?? "")
)

function onInput(event: Event) {
  if (isEditable.value) handleInput(event)
}

function onPaste(event: ClipboardEvent) {
  if (isEditable.value) handlePaste(event)
}

const {
  MIN_CHARS,
  rootEl,
  editorEl,
  displayText,
  displaySegments,
  mentionLinkClass,
  mentionTitle,
  mentionAtPointer,
  handleBeforeInput,
  handleMentionClick,
  openMentionAtCaret,
  closeMenu,
  isFocused,
  open,
  loading,
  items,
  atItems,
  atMentionMode,
  rentalProject,
  locationTrigger,
  selectorMode,
  highlightedIndex,
  trimmedQuery,
  mapLocation,
  closeMap,
  onMapSaved,
  usesInlineLocationMap,
  handleInput,
  handleKeydown,
  handlePaste,
  handleFocusout,
  selectSuggestion,
  selectAtSuggestion,
  loadMoreAtSuggestions,
  replaceText,
  insertUploadedFile
} = useMentionEditor({
  text,
  apiKey: computed(() => props.apiKey),
  transformClipboard: props.transformClipboard,
  onInput: (event) => emit("input", event),
  onFileTrigger: () => emit("file-trigger")
})

const hasMentions = computed(() =>
  displaySegments.value.some((segment) => segment.type === "mention")
)
const mentionFrameStyle = ref<CSSProperties>({})
const mentionTextStyle = ref<CSSProperties>({})
// Thicken mention glyphs with text-stroke, keeping the native textarea's character widths.
// A different font-weight would move line breaks, selections and the caret out of alignment.
const hoveredMentionStart = ref<number | null>(null)
const hoveredMention = computed(() =>
  displaySegments.value.find(
    (segment) => segment.type === "mention" && segment.start === hoveredMentionStart.value
  )
)
const hoveredMentionIsSaving = computed(
  () => !!hoveredMention.value && isSavingLocation(hoveredMention.value)
)
const hoveredMentionTitle = computed(() => {
  const segment = hoveredMention.value
  if (segment?.type !== "mention") return undefined
  return hoveredMentionIsSaving.value
    ? "Locatie wordt opgeslagen…"
    : mentionTitle(segment.kind, segment.id, segment.label)
})

function isSavingLocation(segment: (typeof displaySegments.value)[number]) {
  return (
    segment.type === "mention" && segment.kind === "location" && isPendingLocationId(segment.id)
  )
}

function onMentionHover(event: MouseEvent) {
  hoveredMentionStart.value = props.disabled ? null : (mentionAtPointer(event)?.start ?? null)
}

function onEditorScroll() {
  hoveredMentionStart.value = null
  syncMentionDisplay()
}

watch([displayText, () => props.disabled], () => {
  hoveredMentionStart.value = null
})

function syncMentionDisplay() {
  const el = editorEl.value
  if (!el || !hasMentions.value) return
  const styles = window.getComputedStyle(el)
  mentionFrameStyle.value = {
    left: `${el.offsetLeft + el.clientLeft}px`,
    top: `${el.offsetTop + el.clientTop}px`,
    width: `${el.clientWidth}px`,
    height: `${el.clientHeight}px`,
    opacity: styles.opacity
  }
  const textStyles: CSSProperties = {
    boxSizing: "border-box",
    width: `${el.clientWidth}px`,
    whiteSpace: props.wrap === "off" ? "pre" : "pre-wrap",
    overflowWrap: "break-word",
    transform: `translate(${-el.scrollLeft}px, ${-el.scrollTop}px)`
  }
  const textProperties = [
    "fontFamily",
    "fontSize",
    "fontWeight",
    "fontStyle",
    "fontVariant",
    "lineHeight",
    "letterSpacing",
    "wordSpacing",
    "textIndent",
    "textTransform",
    "textAlign",
    "direction",
    "tabSize",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft"
  ] as const
  Object.assign(
    textStyles,
    Object.fromEntries(textProperties.map((property) => [property, styles[property]]))
  )
  mentionTextStyle.value = textStyles
}

useResizeObserver(editorEl, syncMentionDisplay)
watch(
  [
    editorEl,
    displayText,
    hasMentions,
    () => props.size,
    () => props.rows,
    () => props.wrap,
    () => props.disabled
  ],
  () => {
    void nextTick(syncMentionDisplay)
  },
  { flush: "post", immediate: true }
)

watchEffect(
  () => {
    editorEl.value = textarea.value?.textareaRef ?? undefined
  },
  { flush: "sync" }
)

const suggestionsId = `mention-suggestions-${useId()}`
const activeSuggestionId = computed(() => {
  const suggestions = selectorMode.value === "at" ? atItems.value : items.value
  return open.value && isEditable.value && suggestions.length > 0
    ? `${suggestionsId}-${highlightedIndex.value}`
    : undefined
})

function onPopoverOpenChange(value: boolean) {
  if (!value) closeMenu()
}

function onInteractOutside(event: Event) {
  const target = (event as CustomEvent<{ originalEvent: Event }>).detail.originalEvent.target
  if (target instanceof Node && rootEl.value?.contains(target)) event.preventDefault()
}

const atEmptyText = computed(() => {
  if (atMentionMode.value === "project") return "Geen projecten gevonden"
  if (atMentionMode.value === "business") return "Geen bedrijven gevonden"
  if (atMentionMode.value === "rental") return "Geen uitstaande huuritems gevonden"
  return "Geen resultaten gevonden"
})

const dropdownEl = ref<HTMLElement>()
const LOAD_MORE_SCROLL_THRESHOLD_PX = 32

function onDropdownScroll() {
  const el = dropdownEl.value
  if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - LOAD_MORE_SCROLL_THRESHOLD_PX) {
    void loadMoreAtSuggestions()
  }
}

function onKeydown(event: KeyboardEvent) {
  if (
    !props.disabled &&
    (event.ctrlKey || event.metaKey) &&
    event.key === "Enter" &&
    openMentionAtCaret(event)
  ) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  if (!isEditable.value) return
  if (
    open.value &&
    !event.isComposing &&
    ["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(event.key)
  )
    event.stopPropagation()
  handleKeydown(event)
  if (open.value && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
    // Keep the keyboard-highlighted suggestion visible in long (paged) lists.
    void nextTick(() => {
      dropdownEl.value
        ?.querySelector<HTMLElement>('[data-highlighted="true"]')
        ?.scrollIntoView({ block: "nearest" })
    })
  }
}

defineExpose({
  focus: () => textarea.value?.focus(),
  blur: () => textarea.value?.blur(),
  updateSize: () => textarea.value?.autoResize(),
  replaceText,
  insertUploadedFile
})
</script>
